// src/features/fourlink-config/hooks/useChecklists.js

import { useState, useCallback } from 'react';
import { fetchChecklists } from '../api';

export const useChecklists = () => {
  const [checklistsCache, setChecklistsCache] = useState({});
  const [loadingChecklists, setLoadingChecklists] = useState({});
  const [errorChecklists, setErrorChecklists] = useState({});

  const loadChecklist = useCallback(async (checklistIds) => {
    if (!checklistIds || checklistIds.length === 0) return null;

    // Convertir a array si es un solo ID
    const ids = Array.isArray(checklistIds) ? checklistIds : [checklistIds];
    const idsKey = ids.join(',');

    // Si ya está en caché, retornar
    if (checklistsCache[idsKey]) {
      return checklistsCache[idsKey];
    }

    // Si ya está cargando, no hacer otra petición
    if (loadingChecklists[idsKey]) {
      return null;
    }

    try {
      setLoadingChecklists(prev => ({ ...prev, [idsKey]: true }));
      setErrorChecklists(prev => ({ ...prev, [idsKey]: null }));

      const data = await fetchChecklists({ ids });
      
      setChecklistsCache(prev => ({ ...prev, [idsKey]: data }));
      return data;

    } catch (error) {
      console.error('Error cargando checklist:', error);
      setErrorChecklists(prev => ({ 
        ...prev, 
        [idsKey]: error.message || 'Error al cargar checklist' 
      }));
      return null;

    } finally {
      setLoadingChecklists(prev => ({ ...prev, [idsKey]: false }));
    }
  }, [checklistsCache, loadingChecklists]);

  const clearChecklistCache = useCallback((checklistIds) => {
    if (!checklistIds) {
      // Limpiar toda la caché
      setChecklistsCache({});
      setErrorChecklists({});
      return;
    }

    const ids = Array.isArray(checklistIds) ? checklistIds : [checklistIds];
    const idsKey = ids.join(',');

    setChecklistsCache(prev => {
      const newCache = { ...prev };
      delete newCache[idsKey];
      return newCache;
    });

    setErrorChecklists(prev => {
      const newErrors = { ...prev };
      delete newErrors[idsKey];
      return newErrors;
    });
  }, []);

  const getChecklistFromCache = useCallback((checklistIds) => {
    if (!checklistIds) return null;
    
    const ids = Array.isArray(checklistIds) ? checklistIds : [checklistIds];
    const idsKey = ids.join(',');
    
    return checklistsCache[idsKey] || null;
  }, [checklistsCache]);

  const isChecklistLoading = useCallback((checklistIds) => {
    if (!checklistIds) return false;
    
    const ids = Array.isArray(checklistIds) ? checklistIds : [checklistIds];
    const idsKey = ids.join(',');
    
    return !!loadingChecklists[idsKey];
  }, [loadingChecklists]);

  const getChecklistError = useCallback((checklistIds) => {
    if (!checklistIds) return null;
    
    const ids = Array.isArray(checklistIds) ? checklistIds : [checklistIds];
    const idsKey = ids.join(',');
    
    return errorChecklists[idsKey] || null;
  }, [errorChecklists]);

  return {
    loadChecklist,
    clearChecklistCache,
    getChecklistFromCache,
    isChecklistLoading,
    getChecklistError,
  };
};